/**
 * SONE web — app root.
 *
 * Routing, session gating and layout. Deliberately the only place that knows
 * about all of those at once; everything else takes what it needs as props.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from 'react';

import { LoginScreen, SetupScreen, SignupScreen, useMessage, ResetScreen,
  SecondFactorRequired,
} from './components/Auth.tsx';
import { FolderView } from './components/FolderView.tsx';
import { mayEdit } from './entryRights.ts';
import { IconRail } from './components/IconRail.tsx';
import { modeOf } from './components/modes.tsx';
import { AccountMenu } from './components/AccountMenu.tsx';
import { InboxPanel, type InboxView } from './components/InboxPanel.tsx';
import { TrashPanel, type TrashView } from './components/TrashPanel.tsx';
import { useInbox } from './hooks/useInbox.ts';
import { SectionNav, resolveSection, type SectionGroup } from './components/SectionNav.tsx';
import { SECTIONS as YOU_SECTIONS } from './components/Settings.tsx';
import { SECTIONS as WORKSPACE_SECTIONS } from './components/WorkspaceSettingsScreen.tsx';
import { ADMIN_SECTIONS } from './components/AdminScreen.tsx';
import type { TrashEntry } from './api/client.ts';
import { MoveDialog } from './components/MoveDialog.tsx';
import { MoveToWorkspaceDialog } from './components/MoveToWorkspaceDialog.tsx';
import { ShareDialog } from './components/ShareDialog.tsx';
import { Trash } from './components/Trash.tsx';
import { SidebarIcon } from './components/icons.tsx';
import { EntryIconView, titleColorStyle } from './components/EntryIconView.tsx';
import { PageStatus, PageView } from './components/PageView.tsx';
import {
  PAGE_TABS,
  RightPanelToggle,
  RightSidebar,
  readRightPanelOpen,
} from './components/RightSidebar.tsx';
import { SearchPanel } from './components/SearchPanel.tsx';
import { SearchScreen } from './components/Search.tsx';
import { LocaleProvider, resolveLocale, useT } from './i18n/useT.tsx';
import { StaleBundleNotice } from './components/StaleBundleNotice.tsx';
import { OPEN_THREAD_EVENT, showComments } from './lib/found.ts';
import { AdminScreen } from './components/AdminScreen.tsx';
import { Settings } from './components/Settings.tsx';
import { SharesScreen } from './components/SharesScreen.tsx';
import { SharesPanel, type SharesView } from './components/SharesPanel.tsx';
import { WorkspaceListScreen } from './components/WorkspaceListScreen.tsx';
import { ModeBar } from './components/ModeBar.tsx';
import { WorkspaceChooser, WorkspacePanel } from './components/WorkspacePanel.tsx';
import { WorkspaceMenu } from './components/WorkspaceMenu.tsx';
import { WorkspaceSettingsScreen } from './components/WorkspaceSettingsScreen.tsx';
import { Sidebar } from './components/Sidebar.tsx';
import { usePage, useSoneClient } from './hooks/useSoneClient.ts';
import { usePageWhereabouts } from './hooks/usePageWhereabouts.ts';
import { useLinkInterception, useRoute } from './hooks/useRoute.ts';
import { usePages } from './hooks/usePages.ts';
import { resolveScheme } from '@sone/core';

import { Instance } from './components/Instance.tsx';
import { useAppearance } from './hooks/useAppearance.ts';

/**
 * One empty theme, not a new one per render (ADR-0123).
 *
 * It is a dependency of the effect that applies the theme, so a fresh `{}` each
 * time would reapply every custom property on every render of the shell.
 */
const EMPTY_THEME: import('@sone/core').WorkspaceTheme = {};
import { useWorkspaceTheme } from './hooks/useWorkspaceTheme.ts';
import { useFavourites } from './hooks/useFavourites.ts';
import { useWatching } from './hooks/useWatching.ts';
import { useCommentMarkStyle } from './hooks/useCommentMarkStyle.ts';
import { ExportDialog } from './components/ExportDialog.tsx';
import { ImportDialog } from './components/ImportDialog.tsx';
import { InboxScreen } from './components/InboxScreen.tsx';
import { useComments } from './hooks/useComments.ts';
import { usePdfMarks } from './hooks/usePdfMarks.ts';
import { useScrolled } from './hooks/useScrolled.ts';
import { useSession } from './hooks/useSession.ts';
import { useSidebar } from './hooks/useSidebar.ts';
import { asInternalRequest, guestKey } from '@sone/core';
import type { CommentThread, EntryCover, Role } from '@sone/core';
import type { CommentAnchor, DrawnThread } from '@sone/editor';

import { api, type PageNode, type WorkspaceMember } from './api/client.ts';
import { MOVED_SETTINGS, paths, type Route } from './routes/paths.ts';
import { ShareTokenProvider, usePageLink } from './routes/pageLink.tsx';
import { AcceptInvitation } from './components/AcceptInvitation.tsx';

export function App(): ReactElement {
  const { route, navigate } = useRoute();
  // The token from the address bar, so a visitor following an internal link
  // keeps their credential — the link itself carries none, on purpose
  // (ADR-0170).
  useLinkInterception(navigate, route.kind === 'share' ? route.token : null);
  const { state, reload, logout, selectWorkspace } = useSession();

  /**
   * The language, for every screen including the ones with nobody signed in
   * (ADR-0041).
   *
   * Resolved here, above the branches, so the sign-in and setup screens are
   * translated too. They have no session to ask, so what they get is the
   * instance's own negotiation of `Accept-Language` — which the server has been
   * doing all along and nothing was using.
   *
   * Signed in, the person's own setting wins over the workspace's, and both win
   * over the browser's.
   */
  const session = state.status === 'authenticated' ? state.session : null;
  const instance = 'instance' in state ? state.instance : null;
  const workspace =
    state.status === 'authenticated'
      ? state.session.workspaces.find((w) => w.id === state.workspaceId)
      : undefined;

  return (
    <LocaleProvider
      initial={resolveLocale(
        session?.user.locale,
        workspace?.default_locale ?? instance?.suggestedLocale,
      )}
      // The tone of the house, chosen by whoever runs the instance. Delivered
      // with `/api/instance` rather than with the session precisely so that the
      // sign-in screen is addressed the same way as everything behind it.
      address={
        (state.status === 'authenticated' ? state.instance : instance)?.addressForm ??
        'informal'
      }
    >
      {/*
        * The instance's own mark, above everything that draws one (ADR-0123).
        *
        * Here rather than threaded as a prop: the mark appears in the rail, in
        * the mode bar on a phone, on the sign-in screen and beside a workspace
        * in the switcher, and four routes to it are four chances to show two
        * different logos on one screen.
        */}
      <Instance
        value={{
          logo: instance?.brand?.logo ?? null,
          /* The second mark, for dark surfaces (ADR-0149). Null is the ordinary
             state: an instance with one mark uses it on every ground. */
          logoOnDark: instance?.brand?.logoOnDark ?? null,
          /* `=== true` rather than truthiness: the field is optional on the
             payload, and an instance that has not loaded must not be an
             instance that promises mail (ADR-0139). */
          canSendMail: instance?.canSendMail === true,
        }}
      >
        <Routes
          state={state}
          route={route}
          navigate={navigate}
          reload={reload}
          logout={logout}
          selectWorkspace={selectWorkspace}
        />
      </Instance>
    </LocaleProvider>
  );
}

/**
 * Every screen, once the language is known.
 *
 * Split from `App` only so the provider can wrap all of it: the branches below
 * return early, and a provider cannot wrap an early return from outside it.
 */
function Routes({
  state,
  route,
  navigate,
  reload,
  logout,
  selectWorkspace,
}: {
  state: ReturnType<typeof useSession>['state'];
  route: Route;
  navigate: (to: string, options?: { replace?: boolean }) => void;
  reload: () => Promise<void> | void;
  logout: () => Promise<void> | void;
  selectWorkspace: (id: string) => void;
}): ReactElement {
  const { t } = useT();
  const message = useMessage();

  /*
   * The look of the place, decided once, above every branch below (ADR-0124).
   *
   * Both of these were lower down before: the theme in `Workspace`, and light
   * or dark in the appearance settings screen — the only caller of
   * `useAppearance`, which is why a signed-out sign-in screen never carried the
   * instance's colours at all.
   *
   * Here because there must be exactly **one** writer of `data-theme`. Applying
   * the instance's answer at this level and refining it inside `Workspace`
   * would be two, and two writers of one attribute is a screen that changes
   * colour a moment after it appears.
   *
   * Hooks, so they run in the same order whatever branch is taken below.
   */
  const instance = 'instance' in state ? state.instance : null;
  const theme = useWorkspaceTheme(
    state.status === 'authenticated' ? state.workspaceId : null,
    instance?.brand?.theme ?? EMPTY_THEME,
  );
  useAppearance(
    resolveScheme(
      state.status === 'authenticated' ? state.session.user.colorScheme : null,
      theme,
    ),
  );

  // --- unauthenticated routes ---------------------------------------------

  if (state.status === 'loading') {
    return (
      <div className="centered">
        <p className="muted">{t('auth.loading')}</p>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="centered">
        <div className="card">
          <h1>{t('auth.cannotStart')}</h1>
          <p className="error">{message(state.code)}</p>
          <button type="button" className="btn" onClick={() => void reload()}>
            {t('auth.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  if (state.status === 'needsSetup') {
    // Setup outranks the requested route: nothing else can work yet, and
    // redirecting would lose the fact that this is a fresh instance.
    return <SetupScreen onDone={() => void reload()} navigate={navigate} />;
  }

  // An invitation followed by somebody who is already signed in.
  //
  // The sign-up route below only renders for anonymous visitors, so this used
  // to do nothing visible: the person landed in their own workspace with no
  // sign the link had meant anything, and the invitation was not consumed.
  if (state.status === 'authenticated' && route.kind === 'signup' && route.invitationToken) {
    return (
      <AcceptInvitation
        token={route.invitationToken}
        navigate={navigate}
        onJoined={(workspaceId) => {
          if (workspaceId) selectWorkspace(workspaceId);
          void reload();
        }}
      />
    );
  }

  if (state.status === 'anonymous') {
    if (route.kind === 'share') {
      return <ShareRoute token={route.token} pageId={route.pageId} />;
    }
    if (route.kind === 'signup') {
      return (
        <SignupScreen
          onDone={(workspaceId) => {
            // Away from the invitation link, then reload.
            //
            // Registering *uses* the invitation, so leaving the token in the
            // address bar meant the accept screen below rendered next, looked
            // the token up, found it spent, and said something went wrong —
            // after everything had gone right.
            //
            // And land where the invitation pointed. Everybody now has a
            // workspace of their own (ADR-0025), so without this somebody who
            // accepted an invitation arrives in their own empty one — a member
            // of the team they joined, looking at nothing to do with it.
            if (workspaceId) selectWorkspace(workspaceId);
            navigate(paths.home());
            void reload();
          }}
          navigate={navigate}
          invitationToken={route.invitationToken}
        />
      );
    }
    /*
     * A reset link lands here, signed out (ADR-0059).
     *
     * Before the login screen, because somebody arriving from a mail has a
     * token in the address and should not be shown a sign-in form for the
     * password they are about to replace.
     */
    if (route.kind === 'reset') {
      return <ResetScreen token={route.token} navigate={navigate} />;
    }

    return (
      <LoginScreen
        onDone={() => void reload()}
        navigate={navigate}
        instance={state.instance}
      />
    );
  }

  // --- authenticated ------------------------------------------------------

  /*
   * The requirement, after the grace period (ADR-0065).
   *
   * Before the workspace, because the point is that nothing else is reachable
   * — including reading. A stolen password that grants read access to a
   * company's notes has granted the thing that mattered.
   *
   * The server refuses the requests anyway; this is so somebody sees why
   * rather than a screen of failures.
   */
  if (state.session?.user.secondFactorStanding?.kind === 'blocked') {
    return <SecondFactorRequired onDone={() => void reload()} />;
  }

  return (
    <Workspace
      workspaceId={state.workspaceId}
      workspaceName={
        state.session.workspaces.find((w) => w.id === state.workspaceId)?.name ??
        'Workspace'
      }
      displayName={state.session.user.displayName}
      session={state.session}
      // Re-read after a setting the whole interface is drawn from (ADR-0124):
      // the resolution lives above this component, so the way to apply a new
      // answer is to let it happen again.
      reloadSession={reload}
      // Whether this instance has a relay, for the controls that only make
      // sense with one (ADR-0126).
      route={route}
      navigate={navigate}
      onSwitchWorkspace={(id, to = paths.home()) => {
        selectWorkspace(id);
        /*
         * Home unless somewhere else is asked for.
         *
         * A page id from the previous workspace is not reachable in the new
         * one, and leaving it in the URL would show a not-found for a page that
         * exists. The Workspaces mode passes its own destination instead
         * (ADR-0070): choosing a workspace there is choosing what to configure,
         * so it stays in the section it was reading rather than walking out of
         * the area it was just used in.
         *
         * A destination that is already the address is not navigated to. The
         * list looks the same before and after — only the chooser under it
         * changed — and a history entry that changes nothing makes Back do
         * nothing.
         */
        if (to !== window.location.pathname) navigate(to);
        // The session response carries the workspace list, so a freshly created
        // workspace has to be picked up before it can be selected.
        void reload();
      }}
      onLogout={() => void logout()}
    />
  );
}

function Workspace({
  workspaceId,
  workspaceName,
  displayName,
  session,
  reloadSession,
  route,
  navigate,
  onSwitchWorkspace,
  onLogout,
}: {
  workspaceId: string;
  workspaceName: string;
  displayName: string;
  session: import('./api/client.ts').SessionInfo;
  /** Re-reads the session, for a setting the whole interface is drawn from. */
  reloadSession: () => Promise<void> | void;
  /** Whether this instance can send mail at all (ADR-0126). */
  route: ReturnType<typeof useRoute>['route'];
  /** With `replace`, for the redirect of an old settings URL (ADR-0032). */
  navigate: (to: string, options?: { replace?: boolean }) => void;
  /** Switch, and land where the caller says — home when it says nothing. */
  onSwitchWorkspace: (workspaceId: string, to?: string) => void;
  onLogout: () => void;
  /* Null while an old settings URL is being replaced: rendering the wrong area
     for one frame would flash a heading nobody asked for. */
}): ReactElement | null {
  const pageLink = usePageLink();
  const message = useMessage();
  const { t } = useT();

  /**
   * What is being searched for, which lives in the URL (ADR-0118).
   *
   * One string, three writers: the field above the tree, the field on the
   * search screen, and every control in the search panel. A copy of it in
   * component state would be a second answer to what is being searched for, and
   * the panel and the field would drift apart the moment either was used.
   *
   * In the URL rather than in a `useState` here because a search is a place: it
   * can be linked to, and `parseRoute` has read `?q=` since the screen existed.
   *
   * `replace` while typing, `push` on Enter. A keystroke is not somewhere to go
   * back to, and pushing one per character would bury whatever came before the
   * search under forty history entries — but the search somebody *committed* is
   * a place they should be able to leave and return to.
   */
  const onSearch = useCallback(
    (query: string, options?: { commit?: boolean }) => {
      navigate(paths.search(query), { replace: options?.commit !== true });
    },
    [navigate],
  );

  // Whether there is anything above the fold, for the line under the bar at the
  // top (ADR-0042).
  const { scrolled, ref: mainRef } = useScrolled();
  const { client, state: connectionState, failure } = useSoneClient({
    workspaceId,
    displayName,
    userId: session.user.id,
  });
  const {
    tree,
    pages,
    createPage,
    archivePage,
    renameEntry,
    setEntryCover,
    moveEntry,
    applyTitle,
    reload: reloadPages,
    loading: pagesLoading,
    error: pagesError,
    // The client, so somebody else's rename, move or new page appears while
    // this tab is looking at it rather than at the next focus (ADR-0096).
  } = usePages(workspaceId, client);
  // The entry a move dialog is open for, if any.
  const [movingId, setMovingId] = useState<string | null>(null);
  /** The entry being moved to another workspace (ADR-0038). */
  const [movingToWorkspaceId, setMovingToWorkspaceId] = useState<string | null>(null);
  const [sharingId, setSharingId] = useState<string | null>(null);
  const {
    favourites,
    ids: favouriteIds,
    toggle: toggleFavourite,
  } = useFavourites(workspaceId);
  /*
   * Which pages are watched (ADR-0064).
   *
   * A separate hook from favourites, because they are separate acts — the star
   * and the bell sit beside each other and mean different things.
   */
  const { ids: watchedIds, toggle: toggleWatching } = useWatching(workspaceId);
  const {
    visible: sidebarVisible,
    isColumn,
    toggle: toggleSidebar,
    close: closeSidebar,
  } = useSidebar(route);

  /*
   * Which mode the shell is in (ADR-0069). The rail picks it, the panel
   * navigates within it, the area to the right shows what the panel selected.
   */
  const mode = modeOf(route.kind);
  // The client, so the bell hears about a notification rather than waiting for
  // somebody to leave the window and come back (ADR-0093).
  const inbox = useInbox(client);
  const [inboxView, setInboxView] = useState<InboxView>({ of: 'unread' });
  const [trashEntries, setTrashEntries] = useState<TrashEntry[] | null>(null);
  const [trashView, setTrashView] = useState<TrashView>('recent');
  /*
   * What is being looked for in the trash (ADR-0071).
   *
   * Held here rather than in the panel, for the same reason the view is: the
   * menu counts what the list shows, and a query the panel kept to itself would
   * be a filter the counts did not know about.
   */
  const [trashQuery, setTrashQuery] = useState('');
  /* Which of the three lists the shares menu chose, and how many are in each —
     counted by the screen, so the menu's numbers cannot disagree with it. */
  const [sharesView, setSharesView] = useState<SharesView>('links');
  const [shareCounts, setShareCounts] = useState<Record<SharesView, number>>({
    links: 0,
    granted: 0,
    received: 0,
  });

  /*
   * Two menus, not one list with two headings (ADR-0072).
   *
   * ADR-0070 took the workspace out of the settings list, leaving you and the
   * server as two groups in one column. Two is still two subjects, and the
   * column was already long enough that "Wo du landest" and "Mailserver" sat
   * six rows apart in it — with more to come on both sides. So they are two
   * areas, which is what the account menu has been calling them all along.
   *
   * One group each, and no group heading: the panel's own title says which area
   * this is, and repeating it over the only group in the column would be the
   * heading saying nothing.
   */
  const settingsGroups: SectionGroup[] = [
    {
      sections: YOU_SECTIONS.map((e) => ({ id: e.id, label: t(e.label), hint: t(e.hint) })),
      hrefFor: (id) => paths.settings(id),
    },
  ];
  const adminGroups: SectionGroup[] = [
    {
      sections: ADMIN_SECTIONS.map((e) => ({ id: e.id, label: t(e.label), hint: t(e.hint) })),
      hrefFor: (id) => paths.admin(id),
    },
  ];
  const settingsCurrentHref =
    route.kind === 'admin'
      ? paths.admin(resolveSection(ADMIN_SECTIONS, route.section))
      : paths.settings(
          resolveSection(YOU_SECTIONS, route.kind === 'settings' ? route.section : ''),
        );

  /*
   * The workspace the Workspaces mode is talking about.
   *
   * The one in the address while a section is open — an administrator may be
   * configuring one they are not in — and otherwise the one you are in, which
   * is what the chooser should already be showing when you arrive at the list.
   */
  const chosenWorkspaceId =
    route.kind === 'workspaceSettings' ? (route.workspaceId ?? workspaceId) : workspaceId;
  const chosenWorkspaceName =
    session.workspaces.find((one) => one.id === chosenWorkspaceId)?.name ?? workspaceName;
  const workspaceCurrentHref =
    route.kind === 'workspaceSettings'
      ? paths.workspaceSettings(
          resolveSection(WORKSPACE_SECTIONS, route.section),
          chosenWorkspaceId,
        )
      : paths.workspaces();

  /*
   * Built once and given to exactly one place: the rail above the breakpoint,
   * the mode bar below it (ADR-0074). Two mounted copies would be two requests
   * for the same unread count and two answers that can disagree for a moment.
   */
  const accountMenu = (
    <AccountMenu
      displayName={session.user.displayName}
      // A word rather than a name below the breakpoint: the bar gives this a
      // fifth of a phone's width, where a name is an ellipsis (ADR-0074).
      label={isColumn ? undefined : t('mode.you')}
      userId={session.user.id}
      canAdminister={session.user.isInstanceAdmin || session.user.canManageWorkspaces}
      onLogout={onLogout}
    />
  );
  const [rightOpen, setRightOpen] = useState(readRightPanelOpen);

  /*
   * A comment mark in the writing was clicked (ADR-0168).
   *
   * The panel's own half — which tab, and lighting the thread — is its own
   * business; this one owns whether the panel is open at all, so this is all it
   * answers. Three listeners for one event, each holding exactly the state it
   * holds anyway.
   */
  useEffect(() => {
    const open = (): void => setRightOpen(true);
    window.addEventListener(OPEN_THREAD_EVENT, open);
    return () => window.removeEventListener(OPEN_THREAD_EVENT, open);
  }, []);

  // Remembered per browser: reopening the panel on every navigation is the kind
  // of small friction that makes an app feel inattentive.
  useEffect(() => {
    try {
      localStorage.setItem('sone.rightPanel', String(rightOpen));
    } catch {
      // Storage disabled; the panel simply starts closed next time.
    }
  }, [rightOpen]);

  const routePageId = route.kind === 'page' ? route.pageId : null;

  // A folder has no document, so no sync handle is opened for one. Looked up in
  // the tree rather than fetched: the tree already knows every entry's kind, and
  // a request to find out would delay the render for nothing.
  const selected = routePageId ? findNode(tree, routePageId) : null;
  const isFolder = selected?.kind === 'folder';

  /*
   * Which workspace this address lives in (ADR-0175).
   *
   * A page address carries none, on purpose (ADR-0016), and the sync
   * connection is built with one — so an address naming a page elsewhere used
   * to open the right URL on the wrong connection and be told, falsely, that
   * this person no longer has access. The tree answers for free in the ordinary
   * case; the server is asked only for an address the tree does not account
   * for.
   */
  const where = usePageWhereabouts({
    pageId: routePageId,
    inTree: selected !== null,
    treeLoaded: !pagesLoading,
    workspaceId,
  });

  useEffect(() => {
    if (where.status !== 'elsewhere') return;
    /*
     * The same address, on a connection bound to the workspace that holds it.
     *
     * The path alone: the switch keeps the address it is given, and passing one
     * that already matches means no navigation and no history entry that
     * changes nothing — which is what the guard inside it is for. The fragment
     * stays in the address bar untouched, so landing on the block still
     * happens (ADR-0170).
     */
    onSwitchWorkspace(where.workspaceId, window.location.pathname);
  }, [where, onSwitchWorkspace]);

  /*
   * Opened only once its workspace is settled.
   *
   * This is what keeps the connection from ever being asked for a room it will
   * be refused — the refusal that produced the false message above.
   */
  const pageId = isFolder || where.status !== 'here' ? null : routePageId;
  const handle = usePage(client, pageId);
  /**
   * The page's comment threads (ADR-0046).
   *
   * Owned here rather than in the panel, because the editor needs the same list
   * to draw its marks: two readers of one document would disagree about the
   * moment a thread appeared, and a mark over the wrong words is the failure
   * this whole feature is built to avoid.
   */
  /*
   * And the internal ones, in their own document (ADR-0057).
   *
   * Opened through the same `usePage` with the suffixed id, because the store
   * keys entries by the string and passes it through unchanged — the channel
   * name, the open message and the persistence all take it as it is. So a
   * second document needed no client change at all, which I checked rather than
   * assumed.
   *
   * Only for a member: a share-link visitor is refused this room by the server,
   * and asking anyway would produce an error frame on every page they open.
   */
  /*
   * Whether this person could open the internal room at all.
   *
   * The server opens it to somebody the workspace gives its pages to by
   * default — a role with a page level (ADR-0182) — and refuses everybody
   * else, so asking anyway would produce an error frame on every page, and a
   * console full of refusals is how somebody stops reading them.
   *
   * This read `isGuest`, a fact about the account that no invitation ever
   * sets: the guest that exists is a *role*, held by an ordinary account, and
   * the check above let it open the room and the panel then showed the
   * threads. Same rule as the server's, from the same column.
   */
  const canSeeInternal =
    session.workspaces.find((one) => one.id === workspaceId)?.pageLevel != null;
  const internalId = canSeeInternal && pageId ? asInternalRequest(pageId) : null;
  const internalHandle = usePage(client, internalId);

  const comments = useComments(handle?.doc ?? null, session.user.id);
  const internalComments = useComments(internalHandle?.doc ?? null, session.user.id);

  /*
   * The marks on the PDFs in this page (ADR-0152).
   *
   * Beside the comments and not among them: the panel lists what was *said*,
   * and a mark says nothing. Only the page's own document — there is no
   * internal counterpart, because a mark carries no words to be internal about.
   */
  const pdfMarks = usePdfMarks(handle?.doc ?? null, session.user.id);
  /**
   * A selection somebody has pressed Comment on, before they have written
   * anything.
   *
   * Held rather than turned into a thread at once: a thread with an empty first
   * message is a highlight over nothing, and it would arrive on somebody else's
   * screen as exactly that.
   */
  const [pendingComment, setPendingComment] = useState<CommentAnchor | null>(null);
  /** How much a commented passage is marked, and whether at all here. */
  const marks = useCommentMarkStyle();
  /**
   * A past version being read (ADR-0047).
   *
   * Not in the URL. A version is something somebody is looking at for a moment,
   * and a link to one would promise that it still exists when opened — which
   * thinning does not guarantee, and which would make a stale link show a
   * different past than the one that was shared.
   */
  const [viewingVersion, setViewingVersion] = useState<string | null>(null);
  /** A version being compared rather than read (ADR-0053). */
  const [comparingVersion, setComparingVersion] = useState<string | null>(null);
  /** The entry somebody is exporting, if any (ADR-0044). */
  const [exportingId, setExportingId] = useState<string | null>(null);
  /** The entry somebody is importing into, if any (ADR-0044). */
  const [importingId, setImportingId] = useState<string | null>(null);
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  useEffect(() => {
    let cancelled = false;
    void api
      .members(workspaceId)
      .then((result) => {
        if (!cancelled) setMembers(result.members);
      })
      .catch(() => {
        // Names are a nicety: a comment still reads without one.
        if (!cancelled) setMembers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  /*
   * Whether this workspace has already been arrived at (ADR-0072).
   *
   * "Where you land" answers two different questions with one setting, and the
   * difference only shows once the mark is a button somebody presses on
   * purpose. Arriving — a fresh load, a sign-in, a switch of workspace — means
   * the setting in full, including "the page I was last on". Pressing the mark
   * later cannot mean that: you are *on* that page, so the mark would do
   * nothing, which is how it read as broken.
   *
   * So a later press goes to the top of the tree instead. A fixed landing page
   * is honoured either way — somebody who named a page meant that page.
   */
  const arrived = useRef<string | null>(null);

  // The root redirects to a page rather than showing an empty shell.
  useEffect(() => {
    if (route.kind !== 'home') return;
    // Not while the tree is being fetched.
    //
    // Redirecting from a list that is still loading means redirecting to
    // whatever was there before — which after a workspace switch is a page in
    // the workspace just left, and the server refuses it. Correctly, and to
    // somebody who only pressed a switcher.
    if (pagesLoading) return;

    let cancelled = false;
    void api
      .landing(workspaceId)
      .then((landing) => {
        if (cancelled) return;
        // Where they were, or the page they chose — the server has already
        // checked it is still reachable, so this is a page or nothing.
        const first = pages[0]?.id ?? null;
        const again = arrived.current === workspaceId;
        arrived.current = workspaceId;
        /*
         * Coming back to a workspace already visited in this session goes to
         * the top rather than to where somebody was — otherwise pressing the
         * mark bounces them back to the page they just navigated away from.
         *
         * Only for `last`, which is the mode that has that problem (ADR-0119).
         * It used to be "anything but fixed", which was the same thing while
         * there were two modes; `top` and `newest` are answers to "where does
         * this workspace open", and they mean it every time.
         */
        const mode = landing.mode ?? landing.workspace.mode;
        const target = again && mode === 'last' ? first : (landing.landOn ?? first);
        if (!target) return;
        const node = pages.find((page) => page.id === target);
        navigate(pageLink(target, node?.title ?? ''));
      })
      .catch(() => {
        // The first page, as before. A landing preference that cannot be read
        // should cost somebody a good guess, not a blank screen.
        arrived.current = workspaceId;
        const first = pages[0];
        if (first) navigate(pageLink(first.id, first.title ?? undefined));
      });

    return () => {
      cancelled = true;
    };
  }, [route.kind, pages, pagesLoading, navigate, workspaceId]);

  // Remember where they are.
  //
  // On the page rather than on leaving it: a tab closed without warning is the
  // common way a session ends, and an unload handler is the least reliable
  // moment in a browser.
  useEffect(() => {
    if (!routePageId || isFolder) return;
    const timer = setTimeout(() => {
      void api.setLanding(workspaceId, { lastPageId: routePageId }).catch(() => {
        // Where somebody was is worth remembering and not worth reporting.
      });
    }, 2000);
    return () => clearTimeout(timer);
  }, [routePageId, isFolder, workspaceId]);

  const onCreateEntry = async (
    parentPageId: string | null,
    kind: 'page' | 'folder' | 'canvas',
    // A shape to start from, when one was chosen (ADR-0045).
    templateId?: string,
    title = '',
  ): Promise<void> => {
    const id = await createPage({
      title,
      parentPageId,
      kind,
      ...(templateId ? { templateId } : {}),
    });
    // A folder has no document to open, so creating one must not navigate
    // anywhere — it appears in the sidebar and the person carries on.
    //
    // A canvas does open, which it did not before: a page started from a
    // template is a page somebody wants to look at, and so is a board.
    if (id && kind !== 'folder') navigate(pageLink(id));
  };

  // Settings is its own screen, not a page inside the workspace.
  //
  // It was rendered in the content column, which put a sidebar of pages beside
  // a list of instance settings — two navigations for two unrelated things,
  // side by side, and neither of them the one somebody is using. This returns
  // early with a surface of its own and a way back (ADR-0027).
  /*
   * The one thing that still has to happen before the shell renders.
   *
   * An old section id, from a bookmark or an older build. Redirected rather
   * than answered with the first section of the wrong area (ADR-0032): URLs are
   * a public contract, and a link that lands somewhere plausible but wrong is
   * worse than one that lands somewhere right.
   */
  const moved = route.kind === 'settings' ? MOVED_SETTINGS[route.section] : undefined;
  if (moved) {
    navigate(moved, { replace: true });
    return null;
  }

  return (
    <div
      className="app with-sidebar"
      // Drives the grid: a column exists only when it is showing, so the
      // reading column is not narrowed for a panel nobody asked for and the
      // sidebar leaves no empty strip behind when hidden.
      data-right-panel={rightOpen ? 'open' : 'closed'}
      data-sidebar={sidebarVisible ? 'shown' : 'hidden'}
    >
      {/* First in the DOM as well as first in the grid. It is the outermost
          frame of the window, and a screen reader reading the shell in source
          order should meet the map before the tree. */}
      <IconRail here={mode} account={isColumn ? accountMenu : null} unread={inbox.unread} />

      <Sidebar
        mode={mode}
        panelTitle={
          mode === 'search'
            ? t('search.title')
            : mode === 'inbox'
            ? t('inbox.title')
            : mode === 'trash'
              ? t('trash.title')
              : mode === 'shares'
                ? t('shares.title')
              : mode === 'settings'
                ? t('settings.title')
                : mode === 'admin'
                  ? t('area.instance')
                  : t('workspaces.area')
        }
        panelScope={
          mode === 'trash'
            ? t('trash.scope')
            : mode === 'settings'
              ? t('settings.scope')
              : mode === 'admin'
                ? // Said where the changes are made, because this is the one
                  // area where a setting is somebody else's problem too.
                  t('admin.scope')
                : undefined
        }
        /* A mode about one workspace says which with a control rather than a
           line: which workspace is not a fact to read here, it is the choice the
           rest of the column depends on (ADR-0070).
         *
         * Shares and the trash are that kind of mode and said nothing at all
         * (ADR-0114). All three share lists are scoped to one workspace, and the
         * screen named neither it nor a way to change it — so a link created a
         * moment ago in another workspace was simply absent, which reads as the
         * list being broken rather than as it being about somewhere else. The
         * trash has the identical shape and is fixed with it.
         *
         * The inbox deliberately has none: it spans workspaces (ADR-0052) and
         * filters by them in its own panel. A chooser there would be a second
         * answer to a question that screen already answers better. */
        panelChooser={
          mode === 'workspaces' ? (
            <WorkspaceChooser
              chosenId={chosenWorkspaceId}
              chosenName={chosenWorkspaceName}
              chosenIcon={
                session.workspaces.find((one) => one.id === chosenWorkspaceId)?.icon ?? null
              }
              current={workspaceCurrentHref}
              onChoose={onSwitchWorkspace}
            />
          ) : mode === 'shares' || mode === 'trash' || mode === 'search' ? (
            <WorkspaceMenu
              currentIcon={session.workspaces.find((one) => one.id === workspaceId)?.icon ?? null}
              currentId={workspaceId}
              currentName={workspaceName}
              // Stay where you are, looking at the other workspace. The
              // Workspaces mode carries its section across for the same reason:
              // the question did not change, only what it is being asked about.
              onSwitch={(id) => onSwitchWorkspace(id, window.location.pathname)}
              onCreated={(id) => onSwitchWorkspace(id, window.location.pathname)}
            />
          ) : undefined
        }
        panelAction={
          mode === 'inbox' && (inbox.items ?? []).some((one) => !one.read) ? (
            <button
              className="quiet panel-action"
              type="button"
              onClick={() => inbox.markRead()}
            >
              {t('inbox.markAll')}
            </button>
          ) : undefined
        }
        searchQuery={route.kind === 'search' ? route.query : ''}
        onSearch={onSearch}
        onStartExport={setExportingId}
        onStartImport={setImportingId}
        currentIcon={
          session.workspaces.find((w) => w.id === workspaceId)?.icon ?? null
        }
        workspaceId={workspaceId}
        workspaceName={workspaceName}
        onSwitchWorkspace={onSwitchWorkspace}
        tree={tree}
        currentPageId={pageId}
        open={sidebarVisible}
        onClose={closeSidebar}
        onCreatePage={(parent, kind, templateId, title) =>
          void onCreateEntry(parent, kind, templateId, title)
        }
        onRename={(id, title) => void renameEntry(id, title)}
        onStartMove={setMovingId}
        onStartMoveToWorkspace={setMovingToWorkspaceId}
        onStartShare={setSharingId}
        onMove={(id, parent, after) => void moveEntry(id, parent, after)}
        favourites={favourites}
        watchedIds={watchedIds}
        onToggleWatch={(pageId, watching) => void toggleWatching(pageId, watching)}
        favouriteIds={favouriteIds}
        onReloadTree={() => void reloadPages()}
        onToggleFavourite={(id, on) => void toggleFavourite(id, on)}
        onDelete={(id, descendants) => {
          // Confirmed, and the count is in the question. Trashing a folder
          // takes its contents, and someone who has not opened it in a month
          // may not remember what is inside.
          //
          // It says "to the trash" rather than "delete", which is what actually
          // happens: the entry is recoverable for thirty days (ADR-0027). A
          // question that overstates the consequence teaches people to distrust
          // the next one — and this one was also the last English string left in
          // a screen everything else in has been translated.
          const message =
            descendants > 0
              ? t('entry.confirmTrashWithChildren', { count: descendants })
              : t('entry.confirmTrash');
          if (!window.confirm(message)) return;
          void archivePage(id).then(() => {
            // Navigate away if the page being viewed was just deleted, or the
            // editor stays open on something that no longer exists.
            if (route.kind === 'page' && route.pageId === id) navigate(paths.home());
          });
        }}
      >
        {/* The menu, never the content (ADR-0069). Both are computed from the
            list the shell already holds, so the counts beside the names cost
            nothing and cannot disagree with what the list shows. */}
        {mode === 'inbox' && (
          <InboxPanel items={inbox.items} view={inboxView} onPick={setInboxView} />
        )}
        {mode === 'trash' && (
          <TrashPanel
            entries={trashEntries}
            view={trashView}
            query={trashQuery}
            onPick={setTrashView}
            onSearch={setTrashQuery}
          />
        )}
        {mode === 'settings' && (
          <SectionNav groups={settingsGroups} current={settingsCurrentHref} />
        )}
        {mode === 'admin' && (
          <SectionNav groups={adminGroups} current={settingsCurrentHref} />
        )}
        {mode === 'workspaces' && (
          <WorkspacePanel chosenId={chosenWorkspaceId} current={workspaceCurrentHref} />
        )}
        {mode === 'shares' && (
          <SharesPanel counts={shareCounts} view={sharesView} onPick={setSharesView} />
        )}
        {mode === 'search' && (
          <SearchPanel
            workspaceId={workspaceId}
            query={route.kind === 'search' ? route.query : ''}
            onQuery={onSearch}
            folders={tree}
            userId={session.user.id}
          />
        )}
      </Sidebar>

      {/* A failed tree operation used to vanish: usePages recorded the error
          and nothing rendered it, so a refused move looked like a move that
          did not save. Shown as a transient bar rather than a dialog — it is
          information, not a decision. */}
      {pagesError && (
        <div className="app-error" role="status">
          {message(pagesError)}
        </div>
      )}

      <div className="main" ref={mainRef} data-scrolled={scrolled ? 'true' : undefined}>
        <div className="topbar">
          {/* Always present, at every width. It used to be hidden above 800px
              on the theory that a permanent column needs no toggle — which left
              no way to reclaim the space, and no way to undo a collapse. */}
          <button
            className="quiet sidebar-toggle"
            type="button"
            onClick={toggleSidebar}
            aria-label={sidebarVisible ? 'Hide the sidebar' : 'Show the sidebar'}
            aria-expanded={sidebarVisible}
          >
            <SidebarIcon />
          </button>
          <PageStatus
            handle={handle}
            connectionState={connectionState}
            failure={failure}
          />
          <div className="topbar-end">
            <RightPanelToggle open={rightOpen} onToggle={() => setRightOpen((v) => !v)} />
          </div>
        </div>

        {/* Said where it cannot be missed rather than in Settings → About, which
            is the last place anybody looks. */}
        <StaleBundleNotice />
        {/* The requirement, during the grace period (ADR-0065).
          *
          * A banner and not a dialog: everything still works, and a modal for
          * something with a fortnight left is a modal people learn to dismiss
          * without reading. It is not dismissible either — it disappears when
          * the thing is done, which is the only honest way for it to go. */}
        {session.user.secondFactorStanding?.kind === 'grace' && (
          <p className="stale-bundle" role="status">
            {t('required.soon', {
              days: Math.max(
                1,
                Math.ceil(
                  (Date.parse(session.user.secondFactorStanding.deadline) - Date.now()) /
                    86_400_000,
                ),
              ),
            })}{' '}
            <a href={paths.settings('sign-in')}>{t('required.setUp')}</a>
          </p>
        )}

        {route.kind === 'page' && isFolder && selected && (
          <FolderView
            folder={selected}
            trail={ancestorNodes(tree, selected.id)}
            /*
             * Only when they may (ADR-0095).
             *
             * Spread rather than passed-and-ignored, because that is what the
             * optional handlers are for: a caller with nothing to offer cannot
             * hand over one that would be refused. Before the tree carried a
             * role there was nothing to ask, so both went unconditionally — and
             * a member with viewer rights on a folder got a rename field and
             * three buttons that answer 403 (ADR-0092).
             */
            {...(mayEdit(selected)
              ? {
                  onCreate: (parent: string, kind: 'page' | 'folder' | 'canvas') =>
                    void onCreateEntry(parent, kind),
                  onRename: (id: string, title: string) => void renameEntry(id, title),
                  onSetCover: (id: string, cover: EntryCover | null) =>
                    void setEntryCover(id, cover),
                }
              : {})}
          />
        )}

        {route.kind === 'page' && !isFolder && handle && (
          <PageView
            /*
             * Where this page sits, but only while the sidebar is away
             * (ADR-0019's tree is the trail when it is there).
             *
             * A permanent breadcrumb would repeat what the sidebar already
             * shows, on every page, for the sake of the case where it does not.
             * With the sidebar hidden — on a phone, or by choice — a page gives
             * no clue where it is, which is worst for a collection's row: it
             * opens as a page with a name and no visible parent at all.
             */
            trail={sidebarVisible ? [] : ancestorNodes(tree, selected?.id ?? '')}
            // What `[[` may link to, from the list the sidebar already holds
            // (ADR-0173).
            linkablePages={pages}
            // Already fetched for the comments panel: assigning a task needs
            // the same list, and fetching it again per menu opening would be a
            // request for something in hand (ADR-0052).
            members={members}
            connectionState={connectionState}
            handle={handle}
            pageId={routePageId!}
            threads={commentMarksFor(comments.threads)}
            // The threads themselves, for the canvas's marks: they carry the
            // item a thread is about, which the editor's drawn shape does not
            // (ADR-0057).
            itemThreads={comments.threads}
            internalItemThreads={canSeeInternal ? internalComments.threads : []}
            markStyle={marks.style}
            viewingVersion={viewingVersion}
            comparingVersion={comparingVersion}
            onCloseVersion={() => {
              setViewingVersion(null);
              setComparingVersion(null);
            }}
            onComment={(anchor) => {
              // Straight into a thread with an empty first message would be a
              // thread with nothing in it. So the anchor is held, the panel
              // opens, and the thread exists once somebody has actually said
              // something (ADR-0046).
              //
              // Announced rather than opened by hand: the tab has to come
              // forward too, and that is the strip's state, not this one's
              // (ADR-0169). The listener below opens the panel.
              setPendingComment(anchor);
              showComments();
            }}
            /* A mark is the opposite of the line above: there is nothing to
             * wait for, because there is nothing to say. It goes into the
             * document at once and the panel does not open (ADR-0152). */
            onMark={(place, quote) => pdfMarks.add(place, quote)}
            onUnmark={(markId) => pdfMarks.remove(markId)}
            // A signed-in member, so the pair — marking and un-marking — can
            // both be offered.
            mayMark={!session.user.isGuest}
            // Keeps the sidebar in step with the heading as it is typed. The
            // tree comes from the projection over HTTP, so without this it
            // showed the old name until something refetched.
            onTitleChange={(title) => applyTitle(routePageId!, title)}
          />
        )}

        {route.kind === 'page' && !isFolder && !handle && (
          <div className="page-body">
            <p className="muted">Opening…</p>
          </div>
        )}

        {route.kind === 'search' && (
          <SearchScreen workspaceId={workspaceId} query={route.query} onQuery={onSearch} />
        )}

        {route.kind === 'trash' && (
          <Trash
            workspaceId={workspaceId}
            view={trashView}
            query={trashQuery}
            entries={trashEntries}
            onEntries={setTrashEntries}
            tree={tree}
            onChanged={() => void reloadPages()}
            // So somebody else emptying the trash is visible here (ADR-0097).
            client={client}
          />
        )}

        {/* Per workspace, unlike the inbox below: a share is a rule about
            pages, and pages belong to one workspace. The question "what have I
            let out" is asked about a place, not about everything at once. */}
        {route.kind === 'shares' && (
          <SharesScreen
            workspaceId={workspaceId}
            view={sharesView}
            onCounts={setShareCounts}
            // So a link somebody else revoked stops being offered (ADR-0098).
            client={client}
          />
        )}

        {/* The inbox spans workspaces, so it takes no workspace id — the whole
            point is being told about a question asked somewhere other than
            where somebody is standing (ADR-0052). */}
        {route.kind === 'settings' && (
          <Settings
            section={route.section}
            session={session}
            workspaceId={workspaceId}
            reloadSession={reloadSession}
            onClose={() => navigate(paths.home())}
            onLogout={onLogout}
          />
        )}

        {/* The list of workspaces, for everybody (ADR-0067). Outside the
            administration, because it was inside it — which is why a member had
            no list at all and could only edit the one they happened to be
            looking at. The server decides its length. */}
        {route.kind === 'workspaceList' && (
          <WorkspaceListScreen
            session={session}
            workspaceId={workspaceId}
            onClose={() => navigate(paths.home())}
            onLogout={onLogout}
          />
        )}

        {route.kind === 'workspaceSettings' && (
          <WorkspaceSettingsScreen
            section={route.section}
            session={session}
            // The one named in the address, or the one being looked at when the
            // address names none (ADR-0067).
            workspaceId={route.workspaceId ?? workspaceId}
            onClose={() => navigate(paths.home())}
            onLogout={onLogout}
          />
        )}

        {route.kind === 'admin' && (
          <AdminScreen
            section={route.section}
            session={session}
            workspaceId={workspaceId}
            onClose={() => navigate(paths.home())}
            onLogout={onLogout}
          />
        )}

        {route.kind === 'inbox' && (
          <InboxScreen
            /* Everything, and the view with it: the rows are conversations
               rather than notifications now, and grouping has to happen after
               filtering or a thread would be split across views (ADR-0071). */
            items={inbox.items}
            view={inboxView}
            error={inbox.error}
            here={workspaceId}
            onRead={inbox.setRead}
            onSnooze={inbox.snooze}
            onReply={inbox.reply}
            onRemove={inbox.remove}
            /* Switching is part of opening (ADR-0115).
             *
             * The mechanism already existed for the workspace switcher and the
             * inbox did not use it, so a notification about a page in another
             * workspace opened the right address on a connection bound to the
             * wrong one — and the page said "you no longer have access to this
             * page", which was not true and pointed nowhere useful. */
            onGoTo={(id, to) => onSwitchWorkspace(id, to)}
          />
        )}

        {route.kind === 'home' && pages.length === 0 && (
          <div className="page-body">
            <h1>Nothing here yet</h1>
            <button
              className="primary"
              type="button"
              onClick={() => void onCreateEntry(null, 'page')}
            >
              Create your first page
            </button>
          </div>
        )}

        {route.kind === 'notFound' && (
          <div className="page-body">
            <h1>Not found</h1>
            <p className="muted">
              <a href={paths.home()}>Back to your pages</a>
            </p>
          </div>
        )}
      </div>

      {sharingId && (
        <ShareDialog
          workspaceId={workspaceId}
          pageId={sharingId}
          pageTitle={findNode(tree, sharingId)?.title ?? ''}
          // From the open document rather than fetched: the count is only
          // meaningful for the page somebody is looking at, which is the only
          // page this dialog is opened for.
          threadCount={sharingId === pageId ? comments.threads.length : 0}
          // Offering to mail a link where no mail can be sent would be a
          // control that can only ever fail (ADR-0059, ADR-0126).
          onClose={() => setSharingId(null)}
        />
      )}

      {movingToWorkspaceId && (
        <MoveToWorkspaceDialog
          entry={findNode(tree, movingToWorkspaceId)!}
          session={session}
          currentWorkspaceId={workspaceId}
          onCancel={() => setMovingToWorkspaceId(null)}
          onMoved={() => {
            setMovingToWorkspaceId(null);
            // The entry is not in this workspace any more, so the tree is
            // reloaded and — if it was the page being read — the way out is the
            // workspace's own landing page rather than a page that is elsewhere.
            if (pageId === movingToWorkspaceId) navigate(paths.home());
            void reloadPages();
          }}
        />
      )}

      {movingId && (
        <MoveDialog
          entry={findNode(tree, movingId)!}
          tree={tree}
          onCancel={() => setMovingId(null)}
          onMove={(parent) => {
            setMovingId(null);
            void moveEntry(movingId, parent);
          }}
        />
      )}

      {importingId && (
        <ImportDialog
          pageId={importingId}
          title={findNode(tree, importingId)?.title ?? ''}
          onClose={() => setImportingId(null)}
          // The pages exist and the sidebar does not know yet.
          onDone={() => void reloadPages()}
        />
      )}

      {exportingId && (
        <ExportDialog
          pageId={exportingId}
          title={findNode(tree, exportingId)?.title ?? ''}
          onClose={() => setExportingId(null)}
        />
      )}

      <RightSidebar
        handle={handle}
        pageId={pageId}
        workspaceId={workspaceId}
        open={rightOpen}
        onClose={() => setRightOpen(false)}
        comments={comments}
        internalComments={canSeeInternal ? internalComments : null}
        members={members}
        pendingComment={pendingComment}
        onCancelPendingComment={() => setPendingComment(null)}
        marks={marks}
        viewingVersion={viewingVersion}
        onViewVersion={(id) => {
          setViewingVersion(id);
          // Looking at a version and comparing it are two states, not one with a
          // mode: choosing either clears the other rather than leaving both set.
          setComparingVersion(null);
        }}
        onCompareVersion={(id) => {
          setComparingVersion(id);
          setViewingVersion(null);
        }}
        onRevealComment={(thread) => {
          // Resolving a thread's range into editor coordinates is the editor's
          // job, so revealing is a message to it rather than a scroll from
          // here. Nothing to do when the text is gone, and the button that
          // would ask is disabled in that case.
          if (thread.range) {
            window.dispatchEvent(
              new CustomEvent('sone:reveal-comment', { detail: thread.id }),
            );
          }
          /*
           * And a place in a PDF, the same arrangement with a different reader
           * (ADR-0156). Only the viewer knows where page seven currently is —
           * the column has its own scroll, the pages are drawn as they come
           * near, and neither fact belongs up here.
           *
           * The file travels with it because a page may hold two documents, and
           * a message with only a page number would be answered by both.
           */
          if (thread.place) {
            window.dispatchEvent(
              new CustomEvent('sone:reveal-place', {
                detail: {
                  file: thread.place.file,
                  page: thread.place.page,
                  thread: thread.id,
                },
              }),
            );
          }
        }}
      />

      {/* Last in the DOM as well as last on screen (ADR-0074).
        *
        * The rail is first because it is the outermost frame of a window; the
        * bar is the same list of modes at the foot of a phone, and a screen
        * reader reading the shell in source order should meet it where the eye
        * does. Drawn only below 800px, by the stylesheet — the breakpoint has
        * one owner and it is the thing that draws it. */}
      <ModeBar here={mode} account={isColumn ? null : accountMenu} unread={inbox.unread} />
    </div>
  );
}

/**
 * A share link.
 *
 * The workspace is not known before authenticating, so the connection is
 * established with a placeholder and the real id arrives in the auth
 * acknowledgement. This is the one place the URL carries the credential
 * (ADR-0016), and it must work without any account at all.
 */
function ShareRoute({
  token,
  pageId,
}: {
  token: string;
  pageId: string | null;
}): ReactElement {
  const { t } = useT();

  /**
   * The name a visitor gave, remembered for this tab.
   *
   * Asking again on every reload is not a decision, it is an omission: nothing
   * about the name is worth re-deciding, and a page refresh is not a new visit.
   *
   * sessionStorage rather than localStorage, and per token. Per tab, because an
   * anonymous session already belongs to a tab — two tabs are two people as far
   * as presence is concerned, and sharing a name across them would make one
   * person appear twice under one label. Per token, because a different link is
   * a different context, possibly a different circle of people.
   *
   * It is a display name, not a credential, so it does not outlive the browser
   * session on what may be a shared machine.
   */
  const nameKey = `sone.share.name.${token}`;
  const remembered =
    typeof sessionStorage !== 'undefined' ? (sessionStorage.getItem(nameKey) ?? '') : '';

  const [displayName, setDisplayName] = useState(remembered);
  // Skipped entirely when a name is already known, so a reload lands back on
  // the page rather than on a form.
  const [joined, setJoined] = useState(remembered !== '');

  /**
   * Whether this link lets somebody write.
   *
   * The name is asked for so that other people can see who is editing — which
   * makes it pointless on a link that only reads. A reader was being stopped by
   * a form asking them to identify themselves before showing them a page they
   * were invited to read, which is a question with no purpose and, on a page
   * shared with strangers, one they may not want to answer.
   *
   * `null` while the answer is on its way: the form is not shown until it is
   * known, because showing it and then removing it is worse than a moment's
   * wait.
   */
  const [canWrite, setCanWrite] = useState<boolean | null>(null);
  /**
   * The link admits people with accounts, and nobody is signed in (ADR-0101).
   *
   * Its own state rather than a kind of failure: the link is not broken and the
   * page is not gone — there is one thing to do, and the screen can say it.
   */
  const [needsSignIn, setNeedsSignIn] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void api
      .resolveShare(token)
      .then((info) => {
        if (!cancelled && info.requiresSignIn) {
          setNeedsSignIn(true);
          setCanWrite(false);
          return;
        }
        // A password-protected link is resolved after the password, so its role
        // is not known yet — treated as writable, since asking for a name and
        // not needing it is a smaller fault than not asking and being unable to
        // attribute.
        if (!cancelled) setCanWrite(info.requiresPassword || info.role !== 'viewer');
      })
      .catch(() => {
        if (!cancelled) setCanWrite(true);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (canWrite === null && !joined) {
    return (
      <div className="centered">
        <p className="muted">{'…'}</p>
      </div>
    );
  }

  /*
   * Said before the name form rather than after a failed connection.
   *
   * The route used to answer 500 for this, so the screen fell through to "what
   * is your name" and then to a connection error — two wrong screens for a link
   * that is working exactly as its author set it up (ADR-0101).
   */
  if (needsSignIn) {
    return (
      <div className="centered">
        <div className="card">
          <h1>{t('share.signInRequired')}</h1>
          <p className="muted">{t('share.signInRequired.hint')}</p>
          <a className="button" href={paths.login()}>
            {t('auth.signIn')}
          </a>
        </div>
      </div>
    );
  }

  if (!joined && canWrite === true) {
    return (
      <div className="centered">
        <form
          className="card"
          onSubmit={(event) => {
            event.preventDefault();
            const chosen = displayName.trim();
            try {
              if (chosen) sessionStorage.setItem(nameKey, chosen);
            } catch {
              // Private browsing, or storage that is full or disabled. The name
              // still works for this visit; it is simply asked for again next
              // time, which is what happened before this existed.
            }
            setJoined(true);
          }}
        >
          <h1>Shared page</h1>
          <p className="muted">
            Pick a name so other people can see who is editing. No account
            needed.
          </p>
          <div className="field">
            <label htmlFor="name">Your name</label>
            <input
              id="name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Guest"
              autoFocus
            />
          </div>
          <button className="primary" type="submit">
            Open page
          </button>
        </form>
      </div>
    );
  }

  return (
    /*
     * Every link inside keeps the token (ADR-0113).
     *
     * Around the view rather than inside it, so `ShareSession`'s own sidebar is
     * in the context as well: a provider does not reach the component that
     * renders it, and the sidebar is exactly the half that was right before —
     * having it fall back to workspace links would be the same bug the other
     * way round.
     *
     * In context rather than in a prop, because the components that needed it
     * are page **body** content — a folder listing, a collection table, a
     * relation chip — several layers below anybody who knows this is a share
     * view, and threading a token through all of them correctly is the
     * arrangement that produced the bug.
     */
    <ShareTokenProvider token={token}>
      <ShareSession
        token={token}
        pageId={pageId}
        // Empty when nobody was asked, which is a reader — and an empty name
        // records nothing, so a reader leaves no trace in the people panel.
        // That is the correct outcome: they did not write anything to
        // attribute.
        displayName={displayName}
      />
    </ShareTokenProvider>
  );
}

/**
 * How deep an entry sits inside what was shared.
 *
 * Walked from the entry upwards rather than built as a tree, because the list
 * is small — it is one link's scope, not a workspace — and a flat list with an
 * indent reads the same and cannot lose a node whose parent is missing. Which
 * happens legitimately here: a restricted page inside the shared section is
 * withheld, and its children may not be.
 */
function depthOf(
  id: string,
  entries: ReadonlyArray<{ id: string; parentPageId: string | null }>,
): number {
  const byId = new Map(entries.map((one) => [one.id, one]));
  let depth = 0;
  let at = byId.get(id);
  // Bounded by the number of entries, so a cycle — which the database forbids
  // and this cannot verify — stops rather than hangs.
  while (at?.parentPageId && depth < entries.length) {
    at = byId.get(at.parentPageId);
    if (!at) break;
    depth += 1;
  }
  return depth;
}

/**
 * What the link opened on, for the head of the list.
 *
 * The entry with no parent *within what was shared* — which the route sets for
 * the scope page and for nothing else, so this is a lookup rather than a guess.
 * Empty string if the list has not arrived: the head is not a place to put
 * "loading".
 */
function sharedRootTitle(
  entries: ReadonlyArray<{ parentPageId: string | null; title: string }>,
): string {
  return entries.find((one) => one.parentPageId === null)?.title ?? '';
}

/** The kinds the entry icon draws, for a kind that arrives as text. */
function drawnKind(kind: string): 'page' | 'folder' | 'canvas' {
  return kind === 'folder' || kind === 'canvas' ? kind : 'page';
}

/**
 * An entry's chosen icon, narrowed from what the route sends.
 *
 * The shared tree drew `icon={null}` for everything — a plain folder and a
 * plain page — while the title above the page showed the real one. The same
 * entry had two appearances on one screen, and the tree's was the wrong one.
 */
function iconOf(
  icon: unknown,
): { kind: string; value: string; color?: string; titleColor?: string } | null {
  if (!icon || typeof icon !== 'object') return null;
  const record = icon as Record<string, unknown>;
  if (typeof record['kind'] !== 'string' || typeof record['value'] !== 'string') return null;
  return {
    kind: record['kind'],
    value: record['value'],
    ...(typeof record['color'] === 'string' ? { color: record['color'] } : {}),
    // The colour the *title* takes, which is a separate choice from the
    // icon's own and is why the tree needs both.
    ...(typeof record['titleColor'] === 'string' ? { titleColor: record['titleColor'] } : {}),
  };
}

function ShareSession({
  token,
  pageId,
  displayName,
}: {
  token: string;
  pageId: string | null;
  displayName: string;
}): ReactElement {
  const pageLink = usePageLink();
  const { t } = useT();
  /*
   * Open to begin with, and closable — the same as the workspace.
   *
   * Not stored: `useSidebarWidth` keeps the workspace's choice in local
   * storage, and a link is a place somebody arrives once. Remembering that they
   * collapsed the list on a different link, weeks ago, would be a surprise
   * rather than a convenience.
   */
  const [treeOpen, setTreeOpen] = useState(true);
  /*
   * The right panel, closed to begin with.
   *
   * Not `readRightPanelOpen()`: that key is the workspace's, per browser, and a
   * member opening a link in their own browser would find the panel already
   * out — which is the workspace's setting leaking into a view that is not the
   * workspace. A link opens on the page.
   */
  const [rightOpen, setRightOpen] = useState(false);

  /*
   * A comment mark in the writing was clicked (ADR-0168), on a shared link too.
   *
   * The panel's own half — which tab, and lighting the thread — is its own
   * business; this one owns whether the panel is open at all, so this is all it
   * answers. Three listeners for one event, each holding exactly the state it
   * holds anyway.
   */
  useEffect(() => {
    const open = (): void => setRightOpen(true);
    window.addEventListener(OPEN_THREAD_EVENT, open);
    return () => window.removeEventListener(OPEN_THREAD_EVENT, open);
  }, []);
  /*
   * Whether this view has been scrolled (ADR-0163).
   *
   * The workspace has had this since ADR-0042 and a shared link never did, so
   * its bar has been drawing no line at any scroll position — invisible until a
   * cover could run up under the bar, at which point "not scrolled" would have
   * meant "always transparent" and the writing would scroll behind nothing.
   */
  const { scrolled, ref: mainRef } = useScrolled();
  /** A selection waiting for its first message, exactly as in the workspace. */
  const [pendingComment, setPendingComment] = useState<{
    from: Uint8Array;
    to: Uint8Array;
    quote: string;
    item?: string;
  } | null>(null);
  const marks = useCommentMarkStyle();
  // The share token identifies the workspace server-side; the client does not
  // know it yet, so it sends a placeholder that the server ignores in favour of
  // the token's own workspace.
  const { client, state, fatal, passwordRequired, submitPassword } = useSoneClient({
    workspaceId: '00000000-0000-0000-0000-000000000000',
    shareToken: token,
    displayName,
  });
  /**
   * Resolve the token, always.
   *
   * Two jobs, and I first wrote this as though it had one. It supplies the page
   * for a link whose path has none — every link created before the page was
   * added to the URL — *and* it is what sets the share cookie, which is the
   * only credential this visitor has for ordinary HTTP requests.
   *
   * Skipping it when the path already carried a page therefore broke uploads
   * for exactly the links that were supposed to be better: no request, no
   * cookie, and every image upload refused. The page in the path is an
   * optimisation, not a reason to skip the handshake.
   */
  const [resolvedPageId, setResolvedPageId] = useState<string | null>(null);
  const [unresolvable, setUnresolvable] = useState(false);

  useEffect(() => {
    let cancelled = false;

    void api
      .resolveShare(token)
      .then((result) => {
        if (cancelled) return;
        // A password-protected link reports only that a password is needed; the
        // page arrives once the connection is unlocked, which the sync layer
        // handles.
        if (result.pageId) setResolvedPageId(result.pageId);
      })
      .catch(() => {
        // Only fatal when there is no page to fall back on. With one in the
        // path the document still opens; uploads will fail until the cookie is
        // in place, and that is better than refusing to show the page.
        if (!cancelled && pageId === null) setUnresolvable(true);
      });

    return () => {
      cancelled = true;
    };
  }, [token, pageId]);

  const effectivePageId = pageId ?? resolvedPageId;
  const handle = usePage(client, effectivePageId);
  const [password, setPassword] = useState('');

  /*
   * Comments, over the server rather than over the document (ADR-0090).
   *
   * The threads are *read* from the document like everywhere else — a visitor
   * holding a link already has it open. Writing is the half that differs: the
   * sync room's write gate is document-wide, so a commenter's Yjs update would
   * be refused whole, and the transport posts the one thing they may add
   * instead. `useComments` picks the wire; the panel calls one function.
   *
   * Given only when the link actually allows it, so `handle.canComment` decides
   * and this hook does not have to know about roles.
   */
  const commentTransport = useMemo(() => {
    if (!effectivePageId) return null;
    const pageId = effectivePageId;
    return {
      start: async (input: {
        from: Uint8Array;
        to: Uint8Array;
        quote: string;
        item?: string;
        text: string;
      }): Promise<void> => {
        // The name goes with it. The share cookie names the link, not the
        // person holding it, so this is the only thing that tells the server
        // who signed the comment (ADR-0092).
        await api.startComment(pageId, { ...input, name: displayName });
      },
      reply: async (threadId: string, text: string): Promise<void> => {
        await api.replyToComment(pageId, threadId, text, displayName);
      },
    };
  }, [effectivePageId, displayName]);

  const comments = useComments(
    handle?.doc ?? null,
    // A visitor signs with the name they gave. The consequence ADR-0046 already
    // accepted: two people who both type "Anna" are one name in the thread.
    guestKey(displayName),
    commentTransport,
  );

  /*
   * The names behind the member-written comments on this page — and nothing
   * else.
   *
   * `members` stays empty here on purpose: that list is a directory of everyone
   * who works here. But a thread whose authors have no names is barely a
   * thread, so the server returns names for the people who actually wrote a
   * message on *this* page, shaped like the member rows the panel already
   * knows how to read.
   */
  const [authors, setAuthors] = useState<WorkspaceMember[]>([]);
  useEffect(() => {
    if (!effectivePageId) return;
    let cancelled = false;
    void api
      .sharedCommentAuthors(token, effectivePageId)
      .then((result) => {
        if (cancelled) return;
        setAuthors(
          result.authors.map((one) => ({
            userId: one.id,
            displayName: one.name,
            // Everything else a member row carries is workspace information,
            // and none of it is a name. Absent rather than faked: a placeholder
            // role would be a claim about somebody.
            email: null,
            role: '',
            roleId: null,
            roleName: '',
            isGuest: false,
            joinedAt: '',
          })),
        );
      })
      .catch(() => {
        // A page with no comments answers this too; a failure here costs the
        // names and nothing else, so it is not worth a message.
      });
    return () => {
      cancelled = true;
    };
    /*
     * Re-read whenever a message appears, not whenever a *thread* does.
     *
     * The key was `threads.length`, and a reply does not change it — so the
     * commonest sequence of all went unnamed: a visitor opens a thread (no
     * member has written on the page, so the list is empty), a member answers,
     * and the answer showed as "somebody who is no longer here". The names
     * arrived only if somebody happened to start a second thread.
     */
  }, [
    token,
    effectivePageId,
    comments.threads.reduce((total, thread) => total + thread.messages.length, 0),
  ]);

  /**
   * What else the link reaches.
   *
   * Loaded because a shared **folder** was a shared nothing: this view renders
   * one page, a folder has no body, and there was no navigation here at all —
   * so a link to a section of the handbook arrived as an empty page with a
   * name at the top.
   *
   * Only what the link grants, asked of the server rather than filtered here:
   * a visitor holding a link is not a member and must not learn what else the
   * workspace contains.
   */
  const [shared, setShared] = useState<
    Array<{
      id: string;
      parentPageId: string | null;
      title: string;
      kind: string;
      idx: string;
      icon: unknown;
      /** What the link grants here (ADR-0095). */
      role: Role | null;
    }>
  >([]);
  useEffect(() => {
    let cancelled = false;
    void api
      .sharedPages(token)
      .then((result) => {
        if (!cancelled) setShared(result.pages);
      })
      // Quietly: a link to a single page has nothing to draw here, and a
      // password-protected one answers this only once it is unlocked.
      .catch(() => {
        if (!cancelled) setShared([]);
      });
    return () => {
      cancelled = true;
    };
  }, [token, passwordRequired]);

  if (passwordRequired) {
    return (
      <div className="centered">
        <form
          className="card"
          onSubmit={(event) => {
            event.preventDefault();
            submitPassword(password);
          }}
        >
          <h1>This link is protected</h1>
          <div className="field">
            <label htmlFor="lp">Password</label>
            <input
              id="lp"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
            />
          </div>
          <button className="primary" type="submit">
            Open
          </button>
        </form>
      </div>
    );
  }

  if (fatal || unresolvable) {
    return (
      <div className="centered">
        <div className="card">
          <h1>Link unavailable</h1>
          <p className="muted">
            This link may have been revoked or has expired.
          </p>
        </div>
      </div>
    );
  }

  /*
   * Only when the link reaches more than the page it names.
   *
   * A link to one page has nothing to navigate, and an aside holding a single
   * entry is furniture. This is the difference between sharing a page and
   * sharing a section, and the view should look like whichever one happened.
   */
  const hasTree = shared.length > 1;

  /*
   * The comments tab, only when the link lets somebody take part.
   *
   * `PAGE_TABS` is what a link gets by default and deliberately leaves the
   * discussion out — the four workspace-describing tabs stay out for good. But
   * a **commenter** link exists to invite somebody into the conversation, and
   * the tab is the conversation.
   *
   * A *viewer* link does not get it, and that is the answer to whether sharing
   * needs an option for this: the link's level already says it. `viewer`,
   * `commenter`, `editor` — the middle one is the switch, it has been in the
   * dialog since links existed, and it finally does something (ADR-0090).
   */
  const shareTabs = handle?.canComment ? [...PAGE_TABS, 'comments' as const] : PAGE_TABS;

  /*
   * A folder is a folder here too.
   *
   * This rendered `PageView` for whatever the link opened on, and a folder has
   * no body — so the visitor got the editor's empty page with a caret in it and
   * "Write something, or press / for blocks". Reported as "der Ordner Video
   * wird beim Gast als Seite dargestellt auf die man schreiben kann", and it is
   * as bad as that sounds: an invitation to write into something that cannot
   * hold writing, on a link that may be read-only (ADR-0092).
   *
   * Built from the list the link already carries, so a folder shows what is
   * under it *within the share* — never what is under it in the workspace.
   */
  const openedFolder = ((): PageNode | null => {
    const entry = shared.find((one) => one.id === effectivePageId);
    if (!entry || entry.kind !== 'folder') return null;
    const asNode = (one: (typeof shared)[number], depth: number): PageNode => ({
      id: one.id,
      parentPageId: one.parentPageId,
      collectionId: null,
      idx: one.idx,
      title: one.title,
      icon: iconOf(one.icon),
      kind: drawnKind(one.kind),
      // What the link grants here (ADR-0095). The folder view offers nothing to
      // a visitor either way, and a node whose role said "editor" because
      // nobody filled it in is the kind of placeholder that gets believed later.
      role: one.role,
      archived: false,
      lastEditedAt: '',
      children: shared
        .filter((child) => child.parentPageId === one.id)
        .map((child) => asNode(child, depth + 1)),
      depth,
    });
    return asNode(entry, 0);
  })();

  return (
    /*
     * The same shell as the workspace, minus the rail.
     *
     * `.app` is a grid of one column, so the first version's list became a
     * second *row* — a bar across the whole width, above the page. The column
     * has to be a track on the parent; `with-share-tree` is that track, and it
     * is conditional for the same reason the list is.
     *
     * No `with-sidebar`, so no rail column. The rail holds destinations —
     * inbox, trash, settings, the account — and a visitor holding a link has
     * none of them. A 56px strip of nothing beside the list would be the
     * workspace's furniture rendered for somebody who is not in the workspace.
     */
    <div
      className={hasTree ? 'app with-share-tree' : 'app'}
      data-sidebar={treeOpen ? 'shown' : 'hidden'}
      data-right-panel={rightOpen ? 'open' : 'closed'}
    >
      {hasTree && (
        <>
          {/* The overlay's backdrop, which only draws on a narrow screen. Same
              element and same class as the workspace's, so closing the drawer
              by tapping beside it works here without a second answer to what
              that gesture means. */}
          {treeOpen && (
            <button
              className="scrim"
              type="button"
              aria-label={t('sidebar.close')}
              onClick={() => setTreeOpen(false)}
            />
          )}
          <nav
            className={`sidebar share-tree${treeOpen ? ' open' : ''}`}
            aria-label={t('sidebar.label')}
            {...(treeOpen ? {} : { 'aria-hidden': true, inert: true })}
          >
            <div className="panel-head">
              <div className="sidebar-head">
                {/* What was shared, by name. The workspace puts its switcher
                    here; there is nothing to switch between on a link, so the
                    head says which section this is instead — which is the
                    question somebody opening a link actually has. */}
                <div className="panel-title">{sharedRootTitle(shared)}</div>
                <div className="sidebar-head-actions">
                  <button
                    className="quiet drawer-close"
                    type="button"
                    onClick={() => setTreeOpen(false)}
                    title={t('sidebar.hide')}
                    aria-label={t('sidebar.hide')}
                  >
                    <SidebarIcon />
                  </button>
                </div>
              </div>
            </div>

            <div className="panel-body">
              {shared.map((entry) => (
                <div
                  className="tree-row"
                  data-kind={entry.kind}
                  key={entry.id}
                  /* Depth as an indent on the row, from the flat list.
                   *
                   * The workspace nests its rows so a guide line can be drawn
                   * down a branch. Here the list cannot always be nested: a
                   * restricted page inside the shared section is withheld and
                   * its children are not, so a node whose parent is missing is
                   * a legitimate row — and building a tree would drop it. Same
                   * 18px step, so it reads the same. */
                  style={{
                    marginInlineStart: `${18 * depthOf(entry.id, shared)}px`,
                  }}
                >
                  <span className="tree-twisty" data-placeholder="true" aria-hidden="true" />
                  <a
                    className="tree-link"
                    draggable={false}
                    // Built by the same helper the content uses, so the two
                    // cannot answer differently — which is what they did
                    // (ADR-0113): this string was right and the listing beside
                    // it was not.
                    href={pageLink(entry.id, entry.title ?? undefined)}
                    {...(entry.id === effectivePageId
                      ? { 'aria-current': 'page' as const }
                      : {})}
                  >
                    <EntryIconView icon={iconOf(entry.icon)} kind={drawnKind(entry.kind)} />{' '}
                    <span style={titleColorStyle(iconOf(entry.icon))}>{entry.title}</span>
                  </a>
                </div>
              ))}
            </div>
          </nav>
        </>
      )}
      <div className="main" ref={mainRef} data-scrolled={scrolled ? 'true' : undefined}>
        <div className="topbar">
          {/* The way back, and the reason the close button is safe to offer.
              Only when there is a list to reopen — on a single-page link this
              would toggle nothing. */}
          {hasTree && (
            <button
              className="quiet sidebar-toggle"
              type="button"
              onClick={() => setTreeOpen((open) => !open)}
              aria-label={treeOpen ? t('sidebar.hide') : t('sidebar.show')}
              aria-expanded={treeOpen}
            >
              <SidebarIcon />
            </button>
          )}
          <PageStatus handle={handle} connectionState={state} />
          <div className="topbar-end">
            <RightPanelToggle open={rightOpen} onToggle={() => setRightOpen((v) => !v)} />
          </div>
        </div>
        {openedFolder ? (
          /* Read-only: no rename field and no "new page" buttons. A link may be
             a viewer's, and even an editor link is not a licence to reorganise
             somebody's filing — what a link grants is the page, and creating
             beside it is a different act. */
          <FolderView folder={openedFolder} trail={[]} />
        ) : handle && effectivePageId ? (
          <PageView
            handle={handle}
            pageId={effectivePageId}
            connectionState={state}
            // The guest's half of ADR-0046, built as ADR-0090. The marks, the
            // threads and the button are the workspace's, because a comment is
            // a comment: what differs is the wire the write goes down, and
            // `useComments` is where that is decided.
            threads={commentMarksFor(comments.threads)}
            itemThreads={comments.threads}
            // No internal threads on this path, ever, and said rather than
            // omitted: `ShareSession` never builds an internal document, and
            // the sync room would refuse one to a visitor anyway (ADR-0057).
            internalItemThreads={[]}
            onComment={(anchor) => {
              // The same wire as the workspace shell's (ADR-0169). A link that
              // may comment has the comments tab, so the strip answers here too.
              setPendingComment(anchor);
              showComments();
            }}
            /* Not offered here, and said rather than omitted: a link can only
             * tell two visitors apart by the name they typed (ADR-0046), so a
             * mark could be made and never taken off again — which is worse
             * than one that is not offered (ADR-0152). The two callbacks stay
             * to keep the shape of the surface one shape. */
            onMark={() => undefined}
            onUnmark={() => undefined}
            mayMark={false}
            markStyle={marks.style}
            // No trail here: this path renders a page outside the tree, so
            // there are no folders above it to name.
            trail={[]}
            // No member list on this path: it renders a page outside the
            // workspace shell, which is also why there is no trail. Assigning
            // needs the list, so the control is simply not offered here.
            members={[]}
            /* And nothing to link to, for the same reason and a sharper one: a
               visitor holding a link is not a member and must not learn what
               else the workspace contains (ADR-0026). The empty list rather
               than a filtered one — what a visitor may see is the server's
               answer to give, not this view's. */
            linkablePages={[]}
            viewingVersion={null}
            comparingVersion={null}
            onCloseVersion={() => {}}
          />
        ) : (
          <div className="page-body">
            <p className="muted">Opening…</p>
          </div>
        )}
      </div>

      {/* The page's own panel, and only the page's own tabs — plus the
        * discussion, when the link allows one.
        *
        * `PAGE_TABS`: the outline, the attachments, the pictures, the links
        * out. Not the version history, which names every author and every
        * moment they wrote; not the contributors; not the tasks and who they
        * are assigned to; not the discussion. Those describe the workspace,
        * and a link is not a way into the workspace.
        *
        * Not offered as a setting either. "Show the right sidebar" on a share
        * would either hand a stranger the history and the names, or be a
        * switch whose only power is to hide an outline. The split is a fact
        * about the tabs, so it lives with the tabs (see `PAGE_TABS`). */}
      <RightSidebar
        handle={handle}
        pageId={effectivePageId}
        // The workspace this page belongs to is not something a link tells the
        // client — the token names it server-side. None of `PAGE_TABS` needs
        // it, and passing a placeholder would be a lie the next tab believes.
        workspaceId=""
        open={rightOpen}
        onClose={() => setRightOpen(false)}
        tabs={shareTabs}
        comments={comments}
        // Never. Said out loud rather than left to the default, because an
        // omission reads as an oversight and this one is the rule (ADR-0057).
        internalComments={null}
        // Names for the people who wrote on this page, not the workspace's
        // people. See where `authors` is loaded.
        members={authors}
        pendingComment={pendingComment}
        onCancelPendingComment={() => setPendingComment(null)}
        marks={marks}
        onRevealComment={() => {
          // Nothing to scroll to from here yet: the reveal is the editor's, and
          // this view holds no ref to it. The thread is in the panel either way.
        }}
      />
    </div>
  );
}

/**
 * What the editor needs to draw, from what the panel reads.
 *
 * The two want different shapes — the panel wants messages and quotations, the
 * editor wants two anchors and whether to draw at all — so the page converts
 * rather than either knowing about the other.
 */
function commentMarksFor(threads: CommentThread[]): DrawnThread[] {
  return threads.map((thread) => ({
    id: thread.id,
    from: thread.from,
    to: thread.to,
    resolved: thread.resolved,
  }));
}

/** Find a node anywhere in the tree. */
function findNode(nodes: PageNode[], pageId: string): PageNode | null {
  for (const node of nodes) {
    if (node.id === pageId) return node;
    const found = findNode(node.children, pageId);
    if (found) return found;
  }
  return null;
}

/** Ancestor nodes of an entry, outermost first, for a breadcrumb. */
function ancestorNodes(nodes: PageNode[], pageId: string): PageNode[] {
  const walk = (list: PageNode[], trail: PageNode[]): PageNode[] | null => {
    for (const node of list) {
      if (node.id === pageId) return trail;
      const found = walk(node.children, [...trail, node]);
      if (found) return found;
    }
    return null;
  };
  return walk(nodes, []) ?? [];
}
